import { defineStore } from "pinia";

import serverHttp from "@/utils/http";

export const useIntegrationStore = defineStore("integrations", {
  state: () => ({
    returnLocation: null,
    integrations: [],
    integrationCredentials: [],
    taskDefinitions: [],
  }),
  actions: {
    async fetchIntegrations() {
      try {
        const response = await serverHttp.get(`/integrations/all`);
        this.integrations = response.data;
      } catch (error) {
        console.error("Failed to fetch integrations:", error);
        throw error;
      }
    },
    async fetchIntegrationCredentials() {
      try {
        const response = await serverHttp.get("/integration_credentials/all");
        this.integrationCredentials = response.data;
      } catch (error) {
        console.error("Failed to fetch integration credentials:", error);
        throw error;
      }
    },
    async createIntegrationCredential(integrationId, credential) {
      const body = {
        credential: credential,
        integration: integrationId,
      };
      try {
        const response = await serverHttp.post(
          "integration_credentials/",
          body,
        );
        this.integrationCredentials.push(response.data);
      } catch (error) {
        console.error("Failed to create integration credential:", error);
        throw error;
      }
    },
    async deleteIntegrationCredential(credentialId) {
      const queryParam = new URLSearchParams({ id: credentialId });
      try {
        await serverHttp.delete(`integration_credentials/?${queryParam}`);
        this.integrationCredentials = this.integrationCredentials.filter(
          (cred) => cred.id !== credentialId,
        );
      } catch (error) {
        console.error("Failed to delete integration credential:", error);
        throw error;
      }
    },
    setReturnLocation(location) {
      this.returnLocation = location;
    },
    clearReturnLocation() {
      this.returnLocation = null;
    },
    async sendOAuth2Code(code, integrationShortName) {
      const queryParams = new URLSearchParams({
        code: code,
        integration_short_name: integrationShortName,
      });

      try {
        const response = await serverHttp.post(
          `/integration_credentials/oauth2_code?${queryParams}`,
        );
        await this.fetchIntegrationCredentials();
        return response.data;
      } catch (error) {
        console.error("Error sending OAuth2 code:", error);
        throw error;
      }
    },
    async fetchTaskDefinitions() {
      try {
        const response = await serverHttp.get("/task_definitions/all");
        this.taskDefinitions = response.data;
      } catch (error) {
        console.error("Failed to fetch task definitions:", error);
        throw error;
      }
    },
    setIntegrations(integrations) {
      this.integrations = integrations;
    },
    clearIntegrations() {
      this.integrations = null;
    },
    setIntegrationCredentials(integrationCredentials) {
      this.integrationCredentials = integrationCredentials;
    },
    clearIntegrationCredentials() {
      this.integrationCredentials = null;
    },
    setTaskDefinitions(taskDefinitions) {
      this.taskDefinitions = taskDefinitions;
    },
    clearTaskDefinitions() {
      this.taskDefinitions = null;
    },
  },
  getters: {
    getIntegrations() {
      return this.integrations;
    },

    // Helper getter to get authenticated integrations
    authenticatedIntegrations(state) {
      if (
        !Array.isArray(state.integrations) ||
        !Array.isArray(state.integrationCredentials)
      ) {
        return [];
      }
      return state.integrations.filter((integration) =>
        state.integrationCredentials.some(
          (cred) => cred.integration === integration.id,
        ),
      );
    },

    getAuthenticatedIntegrations() {
      return this.authenticatedIntegrations;
    },

    getIntegrationByShortName: (state) => (shortName) => {
      return state.integrations.find(
        (integration) => integration.short_name === shortName,
      );
    },
    getAuthenticatedTaskDefinitions(state) {
      if (
        !Array.isArray(state.taskDefinitions) ||
        !Array.isArray(state.integrationCredentials) ||
        !Array.isArray(state.integrations)
      ) {
        return [];
      }

      const authenticatedIntegrations = this.authenticatedIntegrations;

      return state.taskDefinitions.filter((taskDefinition) =>
        authenticatedIntegrations.some(
          (integration) => integration.id === taskDefinition.integration,
        ),
      );
    },
  },
  persist: {
    enabled: true,
    strategies: [
      {
        key: "integrations",
        storage: localStorage,
      },
    ],
  },
});
